import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { ApiRequestStatus } from '../../../../model/api-request';
import { GlobalActions } from '../../../../model/global-actions';
import appRoutes from '../../../../routing/app-routes';
import {
  clearInsertSecondaryPhoneNumberError,
  resetInsertSecondaryPhoneNumberRequestState,
  setSecondaryPhoneNumber,
} from '../../../../store/features/user-access/user-registration/user-registration-slice';
import { insertSecondaryPhoneNumber } from '../../../../store/features/user-access/user-registration/user-registration-thunk';
import { UserRegistrationState } from '../../../../store/features/user-access/user-registration/user-registration-types';
import { messagesForInsertPhoneNumberError } from '../../../../store/features/user-access/user-registration/user-registration-utils';
import { useAppSelector } from '../../../redux';

export interface InsertSecondaryPhoneNumberPageLogicCallbacks {
  buttonDisabled: boolean;
  phoneNumber: string;
  insertPhoneNumberRequestStatus: ApiRequestStatus;

  updatePhoneNumber: (value?: string) => void;
  handleSubmit: () => void;
  handleSkip: () => void;
}

export const useInsertSecondaryPhoneNumberPageLogic = (
  actions: GlobalActions
): InsertSecondaryPhoneNumberPageLogicCallbacks => {
  const { navigate, showToast, closeToast } = actions;

  const { formatMessage } = useIntl();
  const dispatch = useDispatch();

  const handleSubmit = useCallback(() => {
    dispatch(insertSecondaryPhoneNumber());
  }, [dispatch]);

  const {
    secondaryPhoneNumber,
    insertSecondaryPhoneNumberRequestStatus,
    insertSecondaryPhoneNumberError,
    phoneNumberAlreadyExisting,
  }: UserRegistrationState = useAppSelector(
    (state) => state.features.userRegistration
  );

  const [buttonDisabled, setButtonDisabled] = useState(
    !secondaryPhoneNumber || secondaryPhoneNumber?.length === 0
  );

  const updatePhoneNumber = useCallback(
    (value: string) => {
      if (buttonDisabled) {
        setButtonDisabled(false);
      }
      dispatch(setSecondaryPhoneNumber(value));
    },
    [dispatch, buttonDisabled]
  );

  useEffect(() => {
    if (insertSecondaryPhoneNumberRequestStatus === 'succeeded') {
      navigate(appRoutes.SignatureAreaUserRegistration);
      dispatch(resetInsertSecondaryPhoneNumberRequestState());
    }
  }, [
    dispatch,
    navigate,
    insertSecondaryPhoneNumberRequestStatus,
    phoneNumberAlreadyExisting,
  ]);

  useEffect(() => {
    if (insertSecondaryPhoneNumberError) {
      const errorMessages = messagesForInsertPhoneNumberError(
        insertSecondaryPhoneNumberError
      );
      showToast({
        type: 'error',
        title: formatMessage(errorMessages.title),
        description:
          errorMessages.description && formatMessage(errorMessages.description),
        close: closeToast,
      });
      dispatch(clearInsertSecondaryPhoneNumberError());
    }
  }, [
    dispatch,
    formatMessage,
    insertSecondaryPhoneNumberError,
    closeToast,
    showToast,
  ]);

  const handleSkip = useCallback(() => {
    navigate(appRoutes.SignatureAreaUserRegistration);
  }, [navigate]);

  return {
    buttonDisabled,
    phoneNumber: secondaryPhoneNumber,
    insertPhoneNumberRequestStatus: insertSecondaryPhoneNumberRequestStatus,
    handleSubmit,
    updatePhoneNumber,
    handleSkip,
  };
};
